import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationService } from '../services/api';

interface Notification {
  id: number;
  type: 'NEW_MESSAGE' | 'TRANSACTION_UPDATE' | 'ADMIN_MESSAGE';
  message: string;
  referenceId: number | null;
  isRead: boolean;
  createdAt: string;
}

type FilterType = 'all' | 'unread' | 'read';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  useEffect(() => {
    fetchNotifications(true);
  }, [filter]);

  const fetchNotifications = async (reset: boolean = false) => {
    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      const response = await notificationService.getNotifications({
        limit: LIMIT,
        offset: currentOffset,
      });

      const newNotifications = response.data.data;

      // Apply client-side filter since backend doesn't have filter param
      let filteredNotifications = newNotifications;
      if (filter === 'unread') {
        filteredNotifications = newNotifications.filter((n: Notification) => !n.isRead);
      } else if (filter === 'read') {
        filteredNotifications = newNotifications.filter((n: Notification) => n.isRead);
      }

      if (reset) {
        setNotifications(filteredNotifications);
        setOffset(0);
      } else {
        setNotifications(prev => [...prev, ...filteredNotifications]);
      }

      setHasMore(newNotifications.length === LIMIT);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      setError('Failed to mark all notifications as read');
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'NEW_MESSAGE' && notification.referenceId) {
      // Check if it's a transaction message or direct message
      if (notification.message.includes('in transaction #')) {
        // Transaction message: navigate to transaction page (referenceId is transactionId)
        navigate(`/marketplace/transaction/${notification.referenceId}`);
      } else {
        // Direct message: navigate to conversation (referenceId is userId)
        navigate(`/messages/${notification.referenceId}`);
      }
    } else if (notification.type === 'TRANSACTION_UPDATE' && notification.referenceId) {
      navigate(`/marketplace/transaction/${notification.referenceId}`);
    } else if (notification.type === 'ADMIN_MESSAGE') {
      // Just mark as read (handled above), no navigation
    }
  };

  const loadMore = () => {
    setOffset(prev => prev + LIMIT);
    fetchNotifications(false);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('time.justNow');
    if (diffMins < 60) return t('time.ago', { time: `${diffMins}m` });
    if (diffHours < 24) return t('time.ago', { time: `${diffHours}h` });
    if (diffDays < 7) return t('time.ago', { time: `${diffDays}d` });

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'NEW_MESSAGE':
        return '💬';
      case 'TRANSACTION_UPDATE':
        return '📦';
      case 'ADMIN_MESSAGE':
        return '📢';
      default:
        return '🔔';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    switch (type) {
      case 'NEW_MESSAGE':
        return t('notifications.type.newMessage');
      case 'TRANSACTION_UPDATE':
        return t('notifications.type.transactionUpdate');
      case 'ADMIN_MESSAGE':
        return t('notifications.type.adminMessage', 'Admin Message');
      default:
        return t('notifications.type.notification');
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading && offset === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('notifications.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">{t('notifications.title')}</h1>
        {unreadCount > 0 && (
          <p className="text-gray-600">
            {t('notifications.unreadCount', { count: unreadCount })}
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Filter and Actions */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {t('notifications.filter.all')}
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg font-medium transition ${filter === 'unread'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {t('notifications.filter.unread')}
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 rounded-lg font-medium transition ${filter === 'read'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {t('notifications.filter.read')}
            </button>
          </div>

          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
            >
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-lg divide-y divide-gray-200">
        {notifications.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-6xl mb-4">🔔</div>
            <p className="text-lg">{t('notifications.empty.all')}</p>
            <p className="text-sm mt-2">
              {filter === 'unread'
                ? t('notifications.empty.unread')
                : filter === 'read'
                  ? t('notifications.empty.read')
                  : t('notifications.empty.default')}
            </p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-6 cursor-pointer hover:bg-gray-50 transition ${!notification.isRead ? 'bg-blue-50' : ''
                  }`}
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <span className="text-4xl">{getNotificationIcon(notification.type)}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded mb-2">
                          {getNotificationTypeLabel(notification.type)}
                        </span>
                        <p className="text-gray-900 break-words">{notification.message}</p>
                      </div>
                      {!notification.isRead && (
                        <div className="flex-shrink-0">
                          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-4 mt-3">
                      <span className="text-sm text-gray-500">
                        {formatTime(notification.createdAt)}
                      </span>
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {t('notifications.markAsRead')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Load More */}
            {hasMore && (
              <div className="p-4">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="w-full py-3 text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? t('notifications.loading') : t('notifications.loadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
